import { DEFAULT_COUNTRY_CODE } from "@nf/constants";
import { forwardRef, useCallback, useState } from "react";
import { DefaultInputComponentProps } from "react-phone-number-input";
import ReactPhoneNumberInput, {
  Country,
  Value,
} from "react-phone-number-input/input";
import {
  getExampleNumber,
  AsYouType,
  getCountryCallingCode,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import cx from "classnames";

import { Input } from "components";
import { CountrySelect } from "./components";
import { getCountryFromValue, parseToPhoneInputValue } from "./utils";
import { PhoneInputValue } from "./types";

import styles from "./PhoneInput.module.scss";

const InputWrapper = forwardRef<
  React.ElementRef<typeof Input>,
  DefaultInputComponentProps & {
    selectedCountry: Country;
  }
>(({ value, selectedCountry, onChange, ...props }, ref) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onChange) {
      return;
    }

    const digits = e.target.value.replace(/\D/g, "");
    const dialCode = getCountryCallingCode(selectedCountry);

    const example = getExampleNumber(selectedCountry, examples);
    const maxDigits = example?.nationalNumber.length ?? Infinity;

    if (digits.length > maxDigits) {
      return;
    }

    const e164 = `+${dialCode}${digits}`;
    const formatter = new AsYouType(selectedCountry);
    const formatted = formatter.input(e164);
    const national = formatted
      .replace(`+${dialCode} `, "")
      .replace(`+${dialCode}`, "")
      .trim();

    e.target.value = national;
    onChange(e);
  };

  return (
    <Input
      ref={ref}
      {...props}
      value={value}
      className={styles.input}
      onChange={handleChange}
    />
  );
});

export interface PhoneInputProps {
  value?: PhoneInputValue;
  className?: string;
  defaultCountry?: Country;
  onChange?: (value: PhoneInputValue | undefined) => void;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    { value, className, defaultCountry = DEFAULT_COUNTRY_CODE, onChange },
    ref
  ) => {
    const e164Value = value
      ? (`${value.prefix}${value.number}` as Value)
      : undefined;

    const [country, setCountry] = useState<Country>(
      getCountryFromValue(e164Value, defaultCountry)
    );

    const handleCountryChange = useCallback(
      (newCountry: Country) => {
        setCountry(newCountry);
        onChange?.(parseToPhoneInputValue(e164Value, newCountry));
      },
      [e164Value, onChange]
    );

    const handlePhoneNumberChange = useCallback(
      (number: Value | undefined) => {
        onChange?.(parseToPhoneInputValue(number, country));
      },
      [country, onChange]
    );

    return (
      <div className={cx(styles.phoneInput, className)}>
        <CountrySelect value={country} onChange={handleCountryChange} />
        <ReactPhoneNumberInput
          ref={ref}
          value={e164Value}
          className={styles.phoneInputBase}
          country={country}
          selectedCountry={country}
          international={true}
          inputComponent={InputWrapper}
          onChange={handlePhoneNumberChange}
        />
      </div>
    );
  }
);

export type { Country };

export default PhoneInput;
